import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Suspense } from 'react';
import { DRAWER_Z_INDEX } from '@constants/z-indices';
import clsx from 'clsx';
import type { MotionProps } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';

import type { DrawerWidth } from '../types';

interface DrawerLayoutProps {
  className?: string;
  drawerContent: ReactNode;
  isOpen: boolean;
  width?: DrawerWidth;
}

/** Shared animation for right-side panels (contract view drawer, block editor activity/details). */
export const rightPanelAnimationProps: MotionProps = {
  animate: {
    x: 0,
  },
  exit: {
    x: '100%',
  },
  initial: {
    x: '100%',
  },
  transition: {
    duration: 0.3,
    ease: 'easeInOut',
  },
};

/**
 * Width classes per drawer preset. `default` keeps the original shared
 * behaviour (`w-1/3` capped at the `--drawer-width` CSS var, 340px).
 * `wide` targets ~440px so dense drawers like Contract Guidance can fit
 * full finding titles and the summary tiles without cramping. The viewport
 * cap (`w-1/3` / `w-2/5`) guarantees the contract viewer on the left never
 * shrinks below ~60% on small screens.
 */
const DRAWER_WIDTH_CLASSES: Record<DrawerWidth, string> = {
  default: 'w-1/3 max-w-drawer',
  wide: 'w-2/5 max-w-[440px]',
};

export const DrawerLayout: FC<PropsWithChildren<DrawerLayoutProps>> = ({
  children,
  className,
  drawerContent,
  isOpen,
  width = 'default',
}) => {
  // overflow-clip (not hidden): the contract column can be wider than this
  // box, and a hidden box is still a scroll container — a block-focus
  // scrollIntoView scrolls it horizontally and drags the absolutely
  // positioned drawer off the right edge. clip forbids scrolling outright.
  // Both axes must be clip: mixing clip with hidden computes back to hidden.
  return (
    <div className={clsx('relative flex h-full w-full overflow-clip', className)} data-testid="side-drawer">
      <div className="relative h-full grow">{children}</div>
      <Suspense>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              {...rightPanelAnimationProps}
              className={clsx(
                'absolute top-0 right-0 flex h-full overflow-y-scroll bg-white',
                'border-info-200 border-l',
                DRAWER_WIDTH_CLASSES[width],
                DRAWER_Z_INDEX,
              )}
            >
              {drawerContent}
            </motion.div>
          )}
        </AnimatePresence>
      </Suspense>
    </div>
  );
};
